/**
 * Client-side utility to resolve a product slug to its default
 * (cheapest) variation's npharmPackageId (SKU), along with
 * quantity limits and tier pricing data.
 *
 * Used by agent add-to-cart flows where only the product slug is known.
 */

export type TierPrice = { qty: number; price: number };

export type ResolvedProduct = {
  sku: string | null;
  variationId: number | null;
  minQty: number;
  maxQty: number;
  price: number;
  tierPrices?: TierPrice[];
  requiresPrescription: boolean;
  /** Display metadata for the resolved (cheapest) variation. */
  name: string;
  slug: string;
  image: string;
  countryCode: string;
  packageLabel: string;
  dosageForm: string;
  isGeneric: boolean;
  requiresFridge: boolean;
  species: string[];
};

const EMPTY_DISPLAY = {
  name: "",
  slug: "",
  image: "",
  countryCode: "",
  packageLabel: "",
  dosageForm: "",
  isGeneric: false,
  requiresFridge: false,
  species: [] as string[],
};

const EMPTY_RESOLVED: ResolvedProduct = {
  sku: null,
  variationId: null,
  minQty: 1,
  maxQty: 999,
  price: 0,
  requiresPrescription: false,
  ...EMPTY_DISPLAY,
};

/**
 * Resolve a product slug to a purchasable variation.
 *
 * When `variationId` is given (the variation the user has actually selected on
 * the product page), that exact variation is returned instead of the cheapest
 * — so an agent "add this to cart" honors the on-screen selection. Omitting it
 * preserves the original cheapest-variation default used by search-only adds.
 */
export async function resolveProductSku(
  slug: string,
  variationId?: number | null,
): Promise<ResolvedProduct> {
  try {
    const resp = await fetch("/api/resolve-sku", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(
        variationId != null ? { slug, variationId } : { slug },
      ),
    });
    if (!resp.ok) return { ...EMPTY_RESOLVED };
    const data = await resp.json();
    return {
      sku: data.sku ?? null,
      variationId: data.variationId ?? null,
      minQty: data.minQty ?? 1,
      maxQty: data.maxQty ?? 999,
      price: data.price ?? 0,
      tierPrices: data.tierPrices,
      requiresPrescription: data.requiresPrescription ?? false,
      name: data.name ?? "",
      slug: data.slug ?? "",
      image: data.image ?? "",
      countryCode: data.countryCode ?? "",
      packageLabel: data.packageLabel ?? "",
      dosageForm: data.dosageForm ?? "",
      isGeneric: data.isGeneric ?? false,
      requiresFridge: data.requiresFridge ?? false,
      species: Array.isArray(data.species) ? data.species : [],
    };
  } catch {
    return { ...EMPTY_RESOLVED };
  }
}
